/**
 * @fileoverview What a calendar item is, in one popup — and, where the person
 * looking may change it, the change.
 *
 * Every calendar in the product opens this one: the organization's Calendar,
 * one class's Calendar tab, a program viewer's, and the instructor's. It reads
 * the same everywhere — the program over the class's name, then when, who,
 * where — so a meeting looks like one thing whichever portal it is seen from.
 *
 * **What it may do is the caller's decision.** `edit` is offered only where a
 * save can actually land (a term Hans holds, by someone allowed to change it);
 * otherwise `locked` says who does change it and where, because an Edit button
 * that fails on save is worse than none. `open` is the door to the class or
 * lesson page, where prep notes and documents live; `secondary` takes Edit's
 * place where there is no Edit (an instructor's "Add prep notes"); `cover`
 * names a substitute (C3): the org Calendar offers it beside Open class, and
 * leads with it for a meeting nobody is down to teach. A viewer passes none
 * of them.
 *
 * **Cancel, not delete.** A cancelled meeting stays on every calendar as "No
 * class", greyed, and can be restored: a family checking Tuesday needs to see
 * that there is no class, and a meeting that simply vanished reads as a bug.
 * The instructor is told either way (`org_announcements`).
 */

import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button, Icon, Tag } from "@/components/studio/primitives";
import { SWATCH } from "@/components/org/MeetingCalendar";
import { zoneAbbreviation } from "@/lib/schedule";

const FIELD =
  "mt-1 block w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[15px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};

const fromMinutes = (total) => {
  const wrapped = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(wrapped % 60).padStart(2, "0")}`;
};

/** "15:30" as "3:30 PM". */
export function clock(hhmm) {
  const total = toMinutes(hhmm);
  if (total === null) return "";
  const h = Math.floor(total / 60);
  return `${((h + 11) % 12) + 1}:${String(total % 60).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

/** "3:15 – 4:15 PM", or "11:30 AM – 12:15 PM" across noon. */
export function timeRange(start, durationMinutes) {
  const from = clock(start);
  if (!from || !durationMinutes) return from;
  const to = clock(fromMinutes(toMinutes(start) + durationMinutes));
  const [fromTime, fromHalf] = from.split(" ");
  return fromHalf === to.split(" ")[1] ? `${fromTime} – ${to}` : `${from} – ${to}`;
}

/** A program's structured `location_address` on one line, or "" without one. */
export function addressLine(location) {
  if (!location || typeof location !== "object") return location || "";
  return ["address_line1", "address_line2", "city", "region", "postal_code", "country"]
    .map((field) => location[field])
    .filter(Boolean)
    .join(", ");
}

/**
 * A meeting from `/org/calendar` (or a viewer's cut of it) as this popup
 * takes it. Everyone teaching the class is named; `instructor_name` is the
 * fallback for a payload from before `instructor_names`. The zone is always
 * said here, because the popup is where someone checks the time.
 */
export function calendarMeeting(m) {
  return {
    title: m.class_name,
    program: m.program_title,
    color: SWATCH[m.colour] || "var(--accent-yellow)",
    date: m.date,
    time_local: m.time_local,
    duration_minutes: m.duration_minutes,
    zone: m.timezone ? zoneAbbreviation(m.timezone, m.date) : undefined,
    status: m.status,
    instructors: (m.instructor_names?.length ? m.instructor_names : [m.instructor_name]).filter(Boolean).join(" and "),
    substitute: m.substitute_name,
    location: m.location,
    room: m.room,
    tags: m.substitute_name ? [{ label: "Substitute", accent: true }] : [],
  };
}

/** "Tuesday, October 6" from a YYYY-MM-DD, without tripping over UTC parsing. */
function longDate(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y || !m || !d) return ymd || "";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

function Row({ icon, children, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <Icon name={icon} size={17} className="mt-[2px] shrink-0 text-[var(--text-tertiary)]" />
      <div className="min-w-0 flex-1 font-sans text-[15px] leading-snug text-[var(--text-primary)]">{children}</div>
    </div>
  );
}

/** The meeting's date and times, and taking it off the calendar. */
function EditForm({ meeting, edit, onDone, onBack }) {
  const [day, setDay] = useState(meeting.date);
  const [start, setStart] = useState(meeting.time_local);
  const [end, setEnd] = useState(fromMinutes(toMinutes(meeting.time_local) + (meeting.duration_minutes || 60)));
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const cancelled = meeting.status === "cancelled";

  const duration = (toMinutes(end) ?? 0) - (toMinutes(start) ?? 0);
  const invalid = !day || !start || !end ? "Choose a date, a start and an end." : duration <= 0 ? "It has to end after it starts." : "";

  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
      onDone();
    } catch (err) {
      setError(err?.message || "Couldn't change this meeting");
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="meeting-edit">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.4fr_1fr_1fr]">
        <label className="font-sans text-[13px] text-[var(--text-secondary)]">
          Date
          <input type="date" className={FIELD} value={day} onChange={(e) => setDay(e.target.value)} data-testid="meeting-edit-date" />
        </label>
        <label className="font-sans text-[13px] text-[var(--text-secondary)]">
          Starts
          <input type="time" className={FIELD} value={start} onChange={(e) => setStart(e.target.value)} data-testid="meeting-edit-start" />
        </label>
        <label className="font-sans text-[13px] text-[var(--text-secondary)]">
          Ends
          <input type="time" className={FIELD} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="meeting-edit-end" />
        </label>
      </div>
      {edit.notice && <p className="font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">{edit.notice}</p>}
      {(error || (invalid && day && start && end)) && (
        <p role="alert" className="font-sans text-[14px] text-[var(--state-danger)]" data-testid="meeting-edit-error">
          {error || invalid}
        </p>
      )}

      {confirming ? (
        <div className="rounded-[14px] border border-[var(--state-danger-border)] p-3" data-testid="meeting-cancel-confirm">
          <p className="font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
            Cancel this meeting? It stays on the calendar as &ldquo;No class&rdquo;, the instructor is told, and you can
            restore it later.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="danger" size="md" disabled={busy} onPress={() => run(edit.onCancel)} data-testid="meeting-cancel-yes">
              Cancel meeting
            </Button>
            <Button variant="bare" disabled={busy} onPress={() => setConfirming(false)}>
              Keep it
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={busy || Boolean(invalid) || cancelled}
              onPress={() => run(() => edit.onSave({ date: day, start_time_local: start, duration_minutes: duration }))}
              data-testid="meeting-edit-save"
            >
              Save changes
            </Button>
            <Button variant="ghost" disabled={busy} onPress={onBack} data-testid="meeting-edit-back">
              Back
            </Button>
          </div>
          {cancelled ? (
            <Button variant="bare" disabled={busy} onPress={() => run(edit.onRestore)} data-testid="meeting-restore">
              Restore meeting
            </Button>
          ) : (
            <Button variant="danger" disabled={busy} onPress={() => setConfirming(true)} data-testid="meeting-cancel">
              Cancel meeting
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @param {{
 *   meeting: null | {
 *     title: string, program?: string, color?: string, kind?: string,
 *     date: string, time_local: string, duration_minutes?: number, zone?: string,
 *     status?: string, instructors?: string, substitute?: string,
 *     location?: string, room?: string, attending?: string, note?: string,
 *     tags?: Array<{label: string, accent?: boolean}>,
 *   },
 *   onClose: Function,
 *   edit?: { onSave: Function, onCancel: Function, onRestore: Function, notice?: string },
 *   locked?: string,
 *   open?: { label: string, onPress: Function },
 *   secondary?: { label: string, icon?: string, onPress: Function, testId?: string },
 *   cover?: { lead: boolean, onPress: Function },
 *   children?: React.ReactNode,
 * }} props
 *   `children` are the caller's own actions (record, write notes), drawn above
 *   Edit and Open.
 */
export default function MeetingDetailsDialog({ meeting, onClose, edit, locked, open, secondary, cover, children }) {
  const [editing, setEditing] = useState(false);
  if (!meeting) return null;
  const cancelled = meeting.status === "cancelled";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="studio max-w-[480px] border-[var(--studio-divider)] bg-[var(--studio-overlay)] text-[var(--text-primary)]"
        data-testid="meeting-details"
      >
        <DialogHeader className="text-left">
          {meeting.program && (
            <div className="flex items-center gap-2 pr-8">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: meeting.color || "var(--accent-yellow)" }} />
              <span className="truncate font-sans text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-tertiary)]" data-testid="meeting-program">
                {meeting.program}
              </span>
            </div>
          )}
          <DialogTitle className={`pr-8 font-serif text-[22px] leading-tight text-[var(--text-primary)] ${cancelled ? "line-through decoration-1" : ""}`}>
            {meeting.title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {editing ? "Change this meeting's date and time, or cancel it" : "Details of this calendar item"}
          </DialogDescription>
        </DialogHeader>

        {(cancelled || meeting.tags?.length > 0) && (
          <div className="-mt-1 flex flex-wrap gap-1.5">
            {cancelled && <Tag>No class</Tag>}
            {(meeting.tags || []).filter((t) => t.label !== "No class").map((t) => (
              <Tag key={t.label} accent={t.accent}>
                {t.label}
              </Tag>
            ))}
          </div>
        )}

        {editing && edit ? (
          <EditForm meeting={meeting} edit={edit} onDone={onClose} onBack={() => setEditing(false)} />
        ) : (
          <>
            <div className="flex flex-col gap-3">
              <Row icon="calendar" testId="meeting-when">
                {longDate(meeting.date)}
                <span className="block font-mono text-[13px] text-[var(--text-secondary)]">
                  {[timeRange(meeting.time_local, meeting.duration_minutes), meeting.zone].filter(Boolean).join(" ")}
                  {meeting.duration_minutes ? ` · ${meeting.duration_minutes} min` : ""}
                </span>
              </Row>
              {(meeting.instructors || meeting.substitute) && (
                <Row icon="user" testId="meeting-who">
                  {meeting.substitute ? (
                    <>
                      Covered by {meeting.substitute}
                      {meeting.instructors && (
                        <span className="block text-[13px] text-[var(--text-tertiary)]">Usually {meeting.instructors}</span>
                      )}
                    </>
                  ) : (
                    meeting.instructors
                  )}
                </Row>
              )}
              {/* The room is what someone walking in needs; the address is
                  for finding the building. */}
              {(meeting.room || meeting.location) && (
                <Row icon="mapPin" testId="meeting-where">
                  {meeting.room || meeting.location}
                  {meeting.room && meeting.location && (
                    <span className="block text-[13px] text-[var(--text-tertiary)]">{meeting.location}</span>
                  )}
                </Row>
              )}
              {meeting.attending && (
                <Row icon="users" testId="meeting-attending">
                  {meeting.attending}
                </Row>
              )}
              {/* Whatever the organization said is unusual about the class. */}
              {meeting.note && (
                <Row icon="info" testId="meeting-note">
                  <span className="whitespace-pre-wrap text-[14px] text-[var(--text-secondary)]">{meeting.note}</span>
                </Row>
              )}
            </div>

            {children}

            {(edit || secondary || open || cover) && (
              <div className="flex flex-wrap gap-2 border-t border-[var(--studio-divider)] pt-4">
                {edit ? (
                  <Button variant="ghost" icon="edit" onPress={() => setEditing(true)} data-testid="meeting-edit-open">
                    Edit
                  </Button>
                ) : (
                  secondary && (
                    <Button variant="ghost" icon={secondary.icon} onPress={secondary.onPress} data-testid={secondary.testId || "meeting-secondary"}>
                      {secondary.label}
                    </Button>
                  )
                )}
                {cover && !cover.lead && (
                  <Button variant="ghost" onPress={cover.onPress} data-testid="meeting-assign">
                    Assign substitute
                  </Button>
                )}
                {open && (
                  <Button
                    variant={cover?.lead ? "ghost" : undefined}
                    iconRight="chevronRight"
                    onPress={open.onPress}
                    data-testid="meeting-open"
                  >
                    {open.label}
                  </Button>
                )}
                {cover?.lead && (
                  <Button onPress={cover.onPress} data-testid="meeting-assign">
                    Assign substitute
                  </Button>
                )}
              </div>
            )}
            {locked && (
              <p className="font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]" data-testid="meeting-locked">
                {locked}
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
